import { ConsolidatedRaceChange } from '@/hooks/useProposalEditor'

/**
 * Display markers carried on a race in `workingProposal.races`. They are NOT
 * editable fields — RacesChangesTable reads them with a strict `=== true`
 * check to pick the row badge ("À supprimer" / "Info"). They must therefore
 * reach `consolidatedRaceChanges[].fields` as RAW booleans, never wrapped in
 * a `{ field, options, currentValue }` ConsolidatedChange (which would make
 * `=== true` always false → the row falls back to the "Existante" badge).
 */
const RACE_DISPLAY_MARKERS = ['_proposedDeletion', '_isExistingUnchanged'] as const

interface RaceConsolidationMeta {
  proposalId: string
  agentName: string
  confidence: number
  createdAt: string
}

/**
 * Build the `consolidatedRaceChanges` array consumed by RacesChangesTable from
 * a single proposal's `workingProposal.races` map.
 *
 * Each editable field is wrapped into a ConsolidatedChange so the table can
 * render value + provenance; the display markers are passed through raw (see
 * RACE_DISPLAY_MARKERS).
 *
 * `racesCurrentData` maps raceId → currentData (DB snapshot), used to fill the
 * "Valeur actuelle" column.
 */
export function buildSimpleConsolidatedRaceChanges(
  races: Record<string, any>,
  racesCurrentData: Record<string, any>,
  meta: RaceConsolidationMeta
): ConsolidatedRaceChange[] {
  return Object.entries(races).map(([raceId, raceData]) => {
    const dataEntries = Object.entries(raceData).filter(
      ([field]) => !(RACE_DISPLAY_MARKERS as readonly string[]).includes(field)
    )

    const fields: Record<string, any> = Object.fromEntries(
      dataEntries.map(([field, value]) => [
        field,
        {
          field,
          options: [
            {
              proposalId: meta.proposalId,
              agentName: meta.agentName,
              proposedValue: value,
              confidence: meta.confidence,
              createdAt: meta.createdAt,
            },
          ],
          currentValue: undefined,
        },
      ])
    )

    // Re-attach the display markers as raw booleans (never wrapped).
    if ((raceData as any)._proposedDeletion === true) fields._proposedDeletion = true
    if ((raceData as any)._isExistingUnchanged === true) fields._isExistingUnchanged = true

    return {
      raceId,
      raceName: (raceData as any).name || 'Course',
      proposalIds: [meta.proposalId],
      originalFields: racesCurrentData[raceId] || {},
      fields,
      userModifications: undefined,
    }
  })
}
